import { createFileRoute, Link } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { CalendarDays, Lock, Mic, RefreshCw, Sparkles } from "lucide-react";
import { useState } from "react";
import { EmptyState } from "@/components/brand/empty-state";
import { ErrorState } from "@/components/brand/error-state";
import { useConfirm } from "@/components/layout/confirm-provider";
import { PageHeader } from "@/components/layout/page-header";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { DraftEditor, useMyDrafts } from "@/features/drafts";
import { PromptCard, useGenerateMine, useMyMonth } from "@/features/my-month";
import { CaptureButtons } from "@/features/notebook";
import { useSessionStore } from "@/lib/auth";

/**
 * The employee's month.
 *
 * The end of the loop: the SMM plans, the voice profile says how this person
 * sounds, and this is where the result arrives. Everything on this page is
 * private to the person reading it.
 *
 * An empty month is three different situations and each wants a different next
 * action, so none of them share a message. Guessing wrong here is worse than
 * saying nothing: someone who has finished their profile and sees "finish your
 * profile" learns the product is not paying attention.
 */
export const Route = createFileRoute("/_authenticated/me/")({
  component: MyMonthPage,
});

function MyMonthPage() {
  const { data, isPending, isError, refetch } = useMyMonth();
  const { data: drafts } = useMyDrafts();
  const [openDraftId, setOpenDraftId] = useState<string | null>(null);
  const openDraft = (drafts ?? []).find((d) => d.id === openDraftId) ?? null;

  if (isPending) return <Skeleton className="h-96 rounded-lg" />;
  if (isError) return <ErrorState onRetry={() => void refetch()} />;

  const monthName = format(parseISO(data.month), "MMMM");
  const has = data.prompts.length > 0;
  // Prompts written before the profile last changed were written from an
  // older version of how this person sounds.
  const behind =
    has &&
    data.generatedAt != null &&
    data.voiceUpdatedAt != null &&
    data.voiceUpdatedAt > data.generatedAt;

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader
        eyebrow="My space"
        title="This month"
        description={
          data.planned && data.theme ? (
            <>
              {monthName} is about <span className="font-medium text-foreground">{data.theme}</span>
              {data.themeNote ? `. ${data.themeNote}` : "."}
              <span className="mt-1.5 flex items-center gap-1.5 text-xs">
                <Lock className="size-3.5 shrink-0" aria-hidden />
                These are yours alone. Nobody else can see them.
              </span>
            </>
          ) : (
            "Your prompts for the month land here."
          )
        }
        actions={
          has ? (
            <span className="flex flex-wrap items-center gap-2">
              <Badge>
                {data.prompts.length} {data.prompts.length === 1 ? "prompt" : "prompts"}
              </Badge>
              <RegenerateMine count={data.prompts.length} left={data.regenerationsLeft ?? 0} />
            </span>
          ) : null
        }
      />

      {/* The one thing to do any day: notice something and keep it. */}
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-card px-4 py-3">
        <p className="text-muted-foreground text-sm">
          Noticed something at work worth keeping? Put it in your Notebook.
        </p>
        <CaptureButtons size="sm" />
      </div>

      {data.events.length > 0 ? (
        <div className="mb-5 flex flex-wrap items-center gap-2">
          <span className="text-muted-foreground text-xs uppercase tracking-[0.08em]">
            Coming up
          </span>
          {data.events.map((event) => (
            <Badge key={`${event.date}-${event.title}`} variant="secondary">
              <CalendarDays /> {format(parseISO(event.date), "d MMM")}: {event.title}
            </Badge>
          ))}
        </div>
      ) : null}

      {behind ? <VoiceChanged /> : null}

      {has ? (
        <>
          <ol className="space-y-3">
            {data.prompts.map((prompt, index) => (
              <li key={prompt.id}>
                <PromptCard
                  prompt={prompt}
                  index={index}
                  onOpenDraft={setOpenDraftId}
                  drafts={(drafts ?? []).filter((d) => d.ideaId === prompt.id)}
                />
              </li>
            ))}
          </ol>
          <p className="mt-5 text-center text-muted-foreground text-sm">
            Pick whichever one you have something real to say about. You do not have to use them
            all, or any.
          </p>
        </>
      ) : (
        <Empty
          voiceReady={data.voice.ready}
          missing={data.voice.missingStages}
          planned={data.planned}
          teamReady={data.teamGeneratedAt != null}
          monthName={monthName}
        />
      )}

      <DraftEditor draft={openDraft} onClose={() => setOpenDraftId(null)} />
    </div>
  );
}

/**
 * Rewrite your own set. Only yours: nobody else's prompts move. Capped per
 * month, and the button says how many are left so the cap is never a
 * surprise on the sixth press.
 */
function RegenerateMine({ count, left }: { count: number; left: number }) {
  const generate = useGenerateMine();
  const confirm = useConfirm();
  if (left <= 0) {
    return (
      <span
        className="text-muted-foreground text-xs"
        title="Fresh prompts come with next month's plan."
      >
        No rewrites left this month
      </span>
    );
  }
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={generate.isPending}
      onClick={async () => {
        const ok = await confirm({
          title: "Rewrite your prompts?",
          description: `Your ${count} current prompts are replaced by a new set written from your voice profile. Drafts you already started keep their prompt. The old set cannot be brought back. You can do this ${left} more time${left === 1 ? "" : "s"} this month.`,
          confirmLabel: "Regenerate my prompts",
        });
        if (ok) generate.mutate({});
      }}
    >
      <RefreshCw className={generate.isPending ? "animate-spin" : undefined} />
      {generate.isPending ? "Writing" : `Regenerate my prompts (${left} left)`}
    </Button>
  );
}

/** Their prompts no longer match their profile: a nudge towards the button above. */
function VoiceChanged() {
  return (
    <Card className="mb-5 border-primary/30 bg-primary/5">
      <CardContent className="flex flex-wrap items-center gap-3">
        <RefreshCw className="size-5 shrink-0 text-primary" aria-hidden />
        <p className="min-w-0 flex-1 text-sm">
          <span className="font-medium text-foreground">Your voice profile changed</span>
          <span className="text-muted-foreground">
            {" "}
            since these were written. Regenerate them for a set that sounds like you now.
          </span>
        </p>
      </CardContent>
    </Card>
  );
}

/** The four reasons a month can be empty, each with its own way forward. */
function Empty({
  voiceReady,
  missing,
  planned,
  teamReady,
  monthName,
}: {
  voiceReady: boolean;
  missing: string[];
  planned: boolean;
  /** The SMM has sent this month's prompts to the team already. */
  teamReady: boolean;
  monthName: string;
}) {
  const generate = useGenerateMine();
  // The SMM reads this page as a recipient of their own plan (SB-40): the
  // same gates, worded for someone who runs the month rather than waits on it.
  const isSmm = useSessionStore((s) => s.user?.role === "smm");

  // The gate first: it is the one thing the person reading this can fix.
  if (!voiceReady) {
    return (
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="space-y-3">
          <div className="flex items-start gap-3">
            <Mic className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
            <div>
              <h2 className="font-semibold text-foreground">
                {isSmm
                  ? "Set up your voice profile to start getting your own monthly ideas."
                  : "Tell us how you sound first"}
              </h2>
              <p className="mt-1 text-muted-foreground text-sm">
                {isSmm
                  ? "Running your team never waits on this. Your own ideas are written from your own words, the same way your team's are. It takes about ten minutes, once."
                  : "Prompts are written from your own words. Without them we would be guessing, and a guessed prompt sounds like everybody else. It takes about ten minutes, once."}
              </p>
              <p className="mt-2 text-muted-foreground text-sm">
                Still to do: <span className="text-foreground">{missing.join(", ")}</span>
              </p>
            </div>
          </div>
          <Button asChild>
            <Link to="/me/voice">Set up my voice profile</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!planned) {
    return isSmm ? (
      <EmptyState
        title={`${monthName} has not been planned yet`}
        description="Set the month's theme and your team's prompts and your own go out together."
        action={
          <Button asChild>
            <Link to="/org/plan">Plan the month</Link>
          </Button>
        }
      />
    ) : (
      <EmptyState
        title={`${monthName} has not been planned yet`}
        description="Your team decides the month's theme before prompts go out. Yours will appear here as soon as they do."
      />
    );
  }

  // The month is planned and the profile is finished, so nothing stands
  // between this person and their prompts except asking: one press, one
  // model call, their own set. Whether the SMM has sent the month to the
  // team yet only changes the wording; it never leaves someone waiting on a
  // second send that nobody knows to do.
  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardContent className="space-y-3">
        <div className="flex items-start gap-3">
          <Sparkles className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
          <div>
            <h2 className="font-semibold text-foreground">
              {teamReady
                ? `Your team's ${monthName} prompts are ready`
                : `${monthName} is planned and your profile is finished`}
            </h2>
            <p className="mt-1 text-muted-foreground text-sm">
              {teamReady
                ? "They went out before your profile was finished. Yours are written from your own words the moment you ask, and nobody else's change."
                : "Your prompts are written from your own words the moment you ask. Your team may also send the month out to everyone, and either way you get the same set."}
            </p>
          </div>
        </div>
        <Button disabled={generate.isPending} onClick={() => generate.mutate({})}>
          <Sparkles className={generate.isPending ? "animate-spin" : undefined} />
          {generate.isPending ? "Writing your prompts" : "Write my prompts"}
        </Button>
      </CardContent>
    </Card>
  );
}
